import { useCallback, useMemo, useState } from "react";

import { FlowVersion, FlowVersionFlowChild } from "src/api/flowTypes";
import { FlowVersionStatus } from "src/clients/flow-api";
import { VersionManageModals } from "src/flow/types";
import { UnpublishedTemplateDependency } from "src/flow/usePublishFlowVersionCallback";

export type BuildOpenModalActionFn = (
  modal: VersionManageModals,
  version?: FlowVersion | undefined,
  unpublishedTemplates?: UnpublishedTemplateDependency[] | undefined,
) => () => void;

export const useFlowVersionModals = () => {
  const [currentModal, setCurrentModal] = useState<VersionManageModals>();
  const [selectedVersion, setSelectedVersion] = useState<FlowVersion>();
  const [unpublishedTemplates, setUnpublishedTemplates] =
    useState<UnpublishedTemplateDependency[]>();

  const buildOpenModalAction: BuildOpenModalActionFn = useCallback(
    (modal, version, unpublishedTemplates) => {
      return () => {
        setSelectedVersion(version);
        setUnpublishedTemplates(unpublishedTemplates);
        setCurrentModal(modal);
      };
    },
    [],
  );

  return useMemo(
    () => ({
      currentModal,
      selectedVersion,
      unpublishedTemplates,
      buildOpenModalAction,
      onOpenManageTrafficModal: buildOpenModalAction(
        "manage_traffic",
        selectedVersion,
      ),
      onAfterLeave: () => {
        setSelectedVersion(undefined);
        setUnpublishedTemplates(undefined);
      },
      onClose: () => {
        setCurrentModal(undefined);
      },
    }),
    [
      currentModal,
      selectedVersion,
      unpublishedTemplates,
      buildOpenModalAction,
      setSelectedVersion,
      setCurrentModal,
    ],
  );
};

const versionHasPublishedParentFlows = (
  version: FlowVersionFlowChild,
): boolean =>
  version.parent_flows?.some((parentFlow) =>
    parentFlow.versions?.some((v) => v.status === FlowVersionStatus.PUBLISHED),
  ) ?? false;

export const useArchiveAction = (
  versions: FlowVersionFlowChild[] | undefined,
  buildOpenModalAction: BuildOpenModalActionFn,
) => {
  const liveParentFlowsMapping = useMemo(
    () =>
      versions
        ? versions.reduce(
            (acc, version) => ({
              ...acc,
              [version.id]: versionHasPublishedParentFlows(version),
            }),
            {} as Record<string, boolean>,
          )
        : {},
    [versions],
  );

  const getArchiveAction = useCallback(
    (version: FlowVersionFlowChild) => {
      return liveParentFlowsMapping[version.id]
        ? buildOpenModalAction("archive_version_live_parents", version)
        : buildOpenModalAction("archive_version", version);
    },
    [liveParentFlowsMapping, buildOpenModalAction],
  );

  return { getArchiveAction };
};
